import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/adminlayout';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/api';
import { PRODUCTION_URL } from '../productionLink/productionLink';

const BanquetOperations = () => {
  const [loading, setLoading] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [bookings, setBookings] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [todayEvents, setTodayEvents] = useState([]);
  const [halls, setHalls] = useState([]);
  const [packages, setPackages] = useState([]);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);
  const [bookingForm, setBookingForm] = useState({
    hall_id: '',
    package_id: '',
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    event_type: 'wedding',
    event_date: '',
    event_time: '',
    end_time: '',
    guest_count: '',
    special_requests: '',
    total_amount: '',
    advance_paid: ''
  });
  const [successMessage, setSuccessMessage] = useState('');

  const colors = {
    primary: '#2D5016',
    accent: '#6ba83e',
    lightGray: '#f8faf9',
    mediumGray: '#e8ede9',
    textDark: '#1a1a1a',
    textLight: '#6b7280',
    white: '#ffffff',
    success: '#4a7c2a',
    warning: '#f59e0b',
    danger: '#ef4444',
    pending: '#f59e0b',
    confirmed: '#4a7c2a',
    completed: '#6b7280',
    cancelled: '#ef4444'
  };

  const isMobile = windowWidth <= 768;
  const isTablet = windowWidth > 768 && windowWidth <= 1024;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const fetchAllData = async () => {
    setLoading(true);
    await Promise.all([
      fetchBookings(),
      fetchUpcomingEvents(),
      fetchTodayEvents(),
      fetchHalls(),
      fetchPackages()
    ]);
    setLoading(false);
  };

  const fetchBookings = async () => {
    try {
      const data = await apiGet('/admin/banquet/operations/bookings');
      if (data.success) setBookings(data.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    }
  };

  const fetchUpcomingEvents = async () => {
    try {
      const data = await apiGet('/admin/banquet/operations/upcoming');
      if (data.success) setUpcomingEvents(data.data);
    } catch (error) {
      console.error('Error fetching upcoming events:', error);
    }
  };

  const fetchTodayEvents = async () => {
    try {
      const data = await apiGet('/admin/banquet/operations/today');
      if (data.success) setTodayEvents(data.data);
    } catch (error) {
      console.error('Error fetching today events:', error);
    }
  };

  const fetchHalls = async () => {
    try {
      const data = await apiGet('/admin/banquet/setup/halls');
      if (data.success) setHalls(data.data.filter(h => h.is_available));
    } catch (error) {
      console.error('Error fetching halls:', error);
    }
  };

  const fetchPackages = async () => {
    try {
      const data = await apiGet('/admin/banquet/setup/packages');
      if (data.success) setPackages(data.data.filter(p => p.is_active));
    } catch (error) {
      console.error('Error fetching packages:', error);
    }
  };

  const handleSubmitBooking = async (e) => {
    e.preventDefault();
    try {
      const url = editingBooking
        ? `${PRODUCTION_URL}/api/admin/banquet/operations/bookings/${editingBooking.id}`
        : `${PRODUCTION_URL}/api/admin/banquet/operations/bookings`;

      const response = await fetch(url, {
        method: editingBooking ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bookingForm)
      });

      const data = await response.json();
      if (data.success) {
        setSuccessMessage(`✅ Booking ${editingBooking ? 'updated' : 'created'} successfully!`);
        setShowBookingModal(false);
        setEditingBooking(null);
        resetForm();
        fetchAllData();
      }
    } catch (error) {
      console.error('Error saving booking:', error);
    }
  };

  const handleEditBooking = (booking) => {
    setEditingBooking(booking);
    setBookingForm({
      hall_id: booking.hall_id,
      package_id: booking.package_id || '',
      customer_name: booking.customer_name,
      customer_phone: booking.customer_phone,
      customer_email: booking.customer_email || '',
      event_type: booking.event_type,
      event_date: booking.event_date.split('T')[0],
      event_time: booking.event_time,
      end_time: booking.end_time || '',
      guest_count: booking.guest_count,
      special_requests: booking.special_requests || '',
      total_amount: booking.total_amount,
      advance_paid: booking.advance_paid
    });
    setShowBookingModal(true);
  };

  const handleDeleteBooking = async (id) => {
    if (!window.confirm('Are you sure you want to delete this booking?')) return;

    try {
      const response = await fetch(`${PRODUCTION_URL}/api/admin/banquet/operations/bookings/${id}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        setSuccessMessage('✅ Booking deleted successfully!');
        fetchAllData();
      }
    } catch (error) {
      console.error('Error deleting booking:', error);
    }
  };

  const updateBookingStatus = async (id, status) => {
    try {
      const response = await fetch(`${PRODUCTION_URL}/api/admin/banquet/operations/bookings/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ booking_status: status })
      });

      const data = await response.json();
      if (data.success) {
        setSuccessMessage(`✅ Status updated to ${status}!`);
        fetchAllData();
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const resetForm = () => {
    setBookingForm({
      hall_id: '',
      package_id: '',
      customer_name: '',
      customer_phone: '',
      customer_email: '',
      event_type: 'wedding',
      event_date: '',
      event_time: '',
      end_time: '',
      guest_count: '',
      special_requests: '',
      total_amount: '',
      advance_paid: ''
    });
  };

  const getStatusColor = (status) => {
    return colors[status] || colors.textLight;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    return timeStr.substring(0, 5);
  };

  const styles = {
    pageHeader: {
      marginBottom: isMobile ? '16px' : '24px',
      padding: isMobile ? '0 4px' : '0'
    },
    pageTitle: {
      fontSize: isMobile ? '20px' : isTablet ? '24px' : '28px',
      fontWeight: '700',
      color: colors.textDark,
      marginBottom: '6px'
    },
    contentCard: {
      background: colors.white,
      padding: isMobile ? '16px' : '24px',
      borderRadius: isMobile ? '14px' : '16px',
      boxShadow: '0 8px 24px rgba(45, 80, 22, 0.08)',
      border: `1px solid ${colors.mediumGray}`,
      margin: isMobile ? '0 4px 16px 4px' : '0 0 24px 0'
    },
    sectionTitle: {
      fontSize: isMobile ? '16px' : '20px',
      fontWeight: '700',
      color: colors.textDark,
      marginBottom: isMobile ? '12px' : '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      justifyContent: 'space-between'
    },
    button: {
      padding: isMobile ? '8px 14px' : '10px 18px',
      borderRadius: '6px',
      border: 'none',
      fontSize: isMobile ? '12px' : '13px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: colors.primary,
      color: colors.white
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: isMobile ? '12px' : '13px'
    },
    tableHeader: {
      background: colors.lightGray,
      fontWeight: '700',
      textAlign: 'left',
      padding: isMobile ? '10px' : '12px',
      borderBottom: `2px solid ${colors.mediumGray}`
    },
    tableCell: {
      padding: isMobile ? '10px' : '12px',
      borderBottom: `1px solid ${colors.lightGray}`
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: isMobile ? '20px' : '40px'
    },
    modalContent: {
      background: colors.white,
      borderRadius: isMobile ? '16px' : '20px',
      padding: isMobile ? '20px' : '32px',
      maxWidth: isMobile ? '100%' : '700px',
      width: '100%',
      maxHeight: '90vh',
      overflowY: 'auto'
    },
    formGroup: {
      marginBottom: '16px'
    },
    label: {
      display: 'block',
      marginBottom: '6px',
      fontSize: '12px',
      fontWeight: '600',
      color: colors.textDark
    },
    input: {
      width: '100%',
      padding: '10px 12px',
      borderRadius: '8px',
      border: `1px solid ${colors.mediumGray}`,
      fontSize: '13px',
      fontFamily: 'inherit'
    },
    select: {
      width: '100%',
      padding: '10px 12px',
      borderRadius: '8px',
      border: `1px solid ${colors.mediumGray}`,
      fontSize: '13px',
      fontFamily: 'inherit',
      background: colors.white
    },
    actionButton: {
      padding: '6px 12px',
      borderRadius: '6px',
      border: 'none',
      fontSize: '11px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginRight: '6px'
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
            <div>Loading banquet operations...</div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {successMessage && (
        <div style={{
          padding: isMobile ? '12px 16px' : '14px 20px',
          borderRadius: '10px',
          marginBottom: '16px',
          fontSize: isMobile ? '13px' : '14px',
          fontWeight: '600',
          background: 'rgba(74, 124, 42, 0.15)',
          color: colors.success,
          border: `2px solid ${colors.success}`,
          margin: isMobile ? '0 4px 12px 4px' : '0 0 16px 0'
        }}>
          {successMessage}
        </div>
      )}

      <div style={styles.pageHeader}>
        <h1 style={styles.pageTitle}>Banquet Operations</h1>
        <p style={{ fontSize: isMobile ? '12px' : '14px', color: colors.textLight }}>
          {isMobile ? 'Bookings & events' : 'Manage banquet bookings, events, and catering operations'}
        </p>
      </div>

      {/* Today's Events */}
      {todayEvents.length > 0 && (
        <div style={styles.contentCard}>
          <h2 style={styles.sectionTitle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>📅</span>
              <span>Today's Events</span>
            </div>
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {todayEvents.map(event => (
              <div
                key={event.id}
                style={{
                  padding: isMobile ? '12px' : '14px',
                  background: colors.lightGray,
                  borderRadius: '8px',
                  borderLeft: `4px solid ${getStatusColor(event.booking_status)}`
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <div>
                    <strong style={{ fontSize: isMobile ? '14px' : '16px' }}>{event.customer_name}</strong>
                    <div style={{ fontSize: '12px', color: colors.textLight, marginTop: '4px' }}>
                      {event.hall_name} • {formatTime(event.event_time)} - {formatTime(event.end_time)}
                    </div>
                  </div>
                  <span style={{
                    padding: '4px 10px',
                    borderRadius: '6px',
                    fontSize: '11px',
                    fontWeight: '600',
                    background: `${getStatusColor(event.booking_status)}20`,
                    color: getStatusColor(event.booking_status),
                    height: 'fit-content'
                  }}>
                    {event.booking_status}
                  </span>
                </div>
                <div style={{ fontSize: '12px', color: colors.textDark }}>
                  <strong>{event.event_type}</strong> • {event.guest_count} guests
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upcoming Events */}
      <div style={styles.contentCard}>
        <h2 style={styles.sectionTitle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>🎉</span>
            <span>Upcoming Events</span>
          </div>
        </h2>
        {upcomingEvents.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '30px', color: colors.textLight }}>
            No upcoming events
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)', gap: '12px' }}>
            {upcomingEvents.map(event => (
              <div
                key={event.id}
                style={{
                  padding: isMobile ? '14px' : '16px',
                  background: colors.lightGray,
                  borderRadius: '10px',
                  border: `1px solid ${colors.mediumGray}`
                }}
              >
                <div style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: '700', marginBottom: '8px', color: colors.primary }}>
                  {event.customer_name}
                </div>
                <div style={{ fontSize: '12px', color: colors.textLight, marginBottom: '8px' }}>
                  {formatDate(event.event_date)} • {formatTime(event.event_time)}
                </div>
                <div style={{ fontSize: '13px', marginBottom: '8px' }}>
                  {event.hall_name}<br />
                  {event.event_type} • {event.guest_count} guests
                </div>
                <span style={{
                  padding: '4px 10px',
                  borderRadius: '6px',
                  fontSize: '11px',
                  fontWeight: '600',
                  background: `${getStatusColor(event.booking_status)}20`,
                  color: getStatusColor(event.booking_status)
                }}>
                  {event.booking_status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* All Bookings */}
      <div style={styles.contentCard}>
        <div style={styles.sectionTitle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>📋</span>
            <span>All Bookings</span>
          </div>
          <button
            onClick={() => {
              setEditingBooking(null);
              resetForm();
              setShowBookingModal(true);
            }}
            style={styles.button}
          >
            + New Booking
          </button>
        </div>

        {bookings.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '30px', color: colors.textLight }}>
            No bookings found
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.tableHeader}>Booking #</th>
                  <th style={styles.tableHeader}>Customer</th>
                  {!isMobile && <th style={styles.tableHeader}>Event</th>}
                  <th style={styles.tableHeader}>Date</th>
                  {!isMobile && <th style={styles.tableHeader}>Hall</th>}
                  <th style={styles.tableHeader}>Guests</th>
                  <th style={styles.tableHeader}>Status</th>
                  <th style={styles.tableHeader}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map(booking => (
                  <tr key={booking.id}>
                    <td style={styles.tableCell}><strong>{booking.booking_number}</strong></td>
                    <td style={styles.tableCell}>{booking.customer_name}</td>
                    {!isMobile && (
                      <td style={styles.tableCell}>{booking.event_type}</td>
                    )}
                    <td style={styles.tableCell}>{formatDate(booking.event_date)}</td>
                    {!isMobile && (
                      <td style={styles.tableCell}>{booking.hall_name}</td>
                    )}
                    <td style={styles.tableCell}>{booking.guest_count}</td>
                    <td style={styles.tableCell}>
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '6px',
                        fontSize: '11px',
                        fontWeight: '600',
                        background: `${getStatusColor(booking.booking_status)}20`,
                        color: getStatusColor(booking.booking_status)
                      }}>
                        {booking.booking_status}
                      </span>
                    </td>
                    <td style={styles.tableCell}>
                      <button
                        onClick={() => handleEditBooking(booking)}
                        style={{
                          ...styles.actionButton,
                          background: colors.accent,
                          color: colors.white
                        }}
                      >
                        ✏️ Edit
                      </button>
                      {booking.booking_status === 'pending' && (
                        <button
                          onClick={() => updateBookingStatus(booking.id, 'confirmed')}
                          style={{
                            ...styles.actionButton,
                            background: colors.success,
                            color: colors.white
                          }}
                        >
                          ✓ Confirm
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Booking Modal */}
      {showBookingModal && (
        <div style={styles.modal} onClick={() => setShowBookingModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ fontSize: isMobile ? '18px' : '22px', marginBottom: '20px', fontWeight: '700' }}>
              {editingBooking ? 'Edit Booking' : 'New Booking'}
            </h3>

            <form onSubmit={handleSubmitBooking}>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px' }}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Hall *</label>
                  <select
                    value={bookingForm.hall_id}
                    onChange={(e) => setBookingForm({ ...bookingForm, hall_id: e.target.value })}
                    style={styles.select}
                    required
                  >
                    <option value="">Select hall...</option>
                    {halls.map(hall => (
                      <option key={hall.id} value={hall.id}>
                        {hall.hall_name} (Capacity: {hall.capacity})
                      </option>
                    ))}
                  </select>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Package (Optional)</label>
                  <select
                    value={bookingForm.package_id}
                    onChange={(e) => setBookingForm({ ...bookingForm, package_id: e.target.value })}
                    style={styles.select}
                  >
                    <option value="">No package</option>
                    {packages.map(pkg => (
                      <option key={pkg.id} value={pkg.id}>
                        {pkg.package_name} (₹{pkg.price_per_person}/person)
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Customer Name *</label>
                <input
                  type="text"
                  value={bookingForm.customer_name}
                  onChange={(e) => setBookingForm({ ...bookingForm, customer_name: e.target.value })}
                  style={styles.input}
                  required
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px' }}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Phone *</label>
                  <input
                    type="tel"
                    value={bookingForm.customer_phone}
                    onChange={(e) => setBookingForm({ ...bookingForm, customer_phone: e.target.value })}
                    style={styles.input}
                    required
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Email</label>
                  <input
                    type="email"
                    value={bookingForm.customer_email}
                    onChange={(e) => setBookingForm({ ...bookingForm, customer_email: e.target.value })}
                    style={styles.input}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px' }}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Event Type *</label>
                  <select
                    value={bookingForm.event_type}
                    onChange={(e) => setBookingForm({ ...bookingForm, event_type: e.target.value })}
                    style={styles.select}
                    required
                  >
                    <option value="wedding">Wedding</option>
                    <option value="corporate">Corporate</option>
                    <option value="birthday">Birthday</option>
                    <option value="conference">Conference</option>
                    <option value="anniversary">Anniversary</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Guest Count *</label>
                  <input
                    type="number"
                    value={bookingForm.guest_count}
                    onChange={(e) => setBookingForm({ ...bookingForm, guest_count: e.target.value })}
                    style={styles.input}
                    required
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 1fr', gap: '12px' }}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Event Date *</label>
                  <input
                    type="date"
                    value={bookingForm.event_date}
                    onChange={(e) => setBookingForm({ ...bookingForm, event_date: e.target.value })}
                    style={styles.input}
                    required
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Start Time *</label>
                  <input
                    type="time"
                    value={bookingForm.event_time}
                    onChange={(e) => setBookingForm({ ...bookingForm, event_time: e.target.value })}
                    style={styles.input}
                    required
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>End Time</label>
                  <input
                    type="time"
                    value={bookingForm.end_time}
                    onChange={(e) => setBookingForm({ ...bookingForm, end_time: e.target.value })}
                    style={styles.input}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px' }}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Total Amount (₹) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={bookingForm.total_amount}
                    onChange={(e) => setBookingForm({ ...bookingForm, total_amount: e.target.value })}
                    style={styles.input}
                    required
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Advance Paid (₹)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={bookingForm.advance_paid}
                    onChange={(e) => setBookingForm({ ...bookingForm, advance_paid: e.target.value })}
                    style={styles.input}
                  />
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Special Requests</label>
                <textarea
                  value={bookingForm.special_requests}
                  onChange={(e) => setBookingForm({ ...bookingForm, special_requests: e.target.value })}
                  style={{ ...styles.input, minHeight: '80px' }}
                />
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                <button type="submit" style={{ ...styles.button, flex: 1 }}>
                  {editingBooking ? 'Update Booking' : 'Create Booking'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowBookingModal(false)}
                  style={{ ...styles.button, background: colors.mediumGray, color: colors.textDark, flex: 1 }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default BanquetOperations;